import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buildFileUrl } from "@/components/common/FileUploader";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const emptyBook = {
  id: "",
  title: "",
  author_id: "",
  publisher_id: "",
  category_id: "",
  rack_id: "",
  code_isbn: "",
  total_qty: "0",
  available_qty: "",
  price: "0",
};

const emptyMember = {
  name: "",
  member_role: "student",
  student_id: "",
  staff_id: "",
  class_id: "",
  section_id: "",
  member_code: "",
  max_active_issues: "",
};

function normalizeImage(url = "") {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("data:")
  )
    return raw;
  if (raw.startsWith("/")) return raw;
  // Student documents often store file IDs; resolve them through files API URL.
  if (/^[a-zA-Z0-9_-]+$/.test(raw) && !raw.includes(".")) {
    return buildFileUrl(raw);
  }
  return `/uploads/${raw}`;
}

function extractImageValue(source) {
  if (!source) return "";
  if (Array.isArray(source)) {
    for (const item of source) {
      const found = extractImageValue(item);
      if (found) return found;
    }
    return "";
  }
  if (typeof source === "object") {
    const directKeys = [
      "photo_url",
      "photo",
      "image",
      "avatar",
      "file_id",
      "id",
    ];
    for (const key of directKeys) {
      const value = source[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    const nestedKeys = [
      "student_photo",
      "doc_student_photo",
      "documents",
      "payload_json",
    ];
    for (const key of nestedKeys) {
      const value = extractImageValue(source[key]);
      if (value) return value;
    }
    return "";
  }
  if (typeof source === "string") {
    const raw = source.trim();
    if (!raw) return "";
    if (
      (raw.startsWith("{") && raw.endsWith("}")) ||
      (raw.startsWith("[") && raw.endsWith("]"))
    ) {
      try {
        return extractImageValue(JSON.parse(raw));
      } catch {
        return raw;
      }
    }
    return raw;
  }
  return "";
}

function makeMemberCode(role, person = {}) {
  if (person.member_code) return String(person.member_code);
  if (role === "student") {
    return String(
      person.admission_no || person.roll_no || `STU-${person.id || "0000"}`,
    );
  }
  return String(
    person.staff_id || person.staff_no || `STF-${person.id || "0000"}`,
  );
}

function SearchableSelect({
  label,
  items,
  value,
  onChange,
  placeholder = "Search...",
  noResultsText = "No matching records",
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => (items || []).find((x) => String(x.id) === String(value)) || null,
    [items, value],
  );

  useEffect(() => {
    if (selected && q.trim() === "") {
      setQ(selected.name || "");
    }
  }, [selected]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = items || [];
    if (!needle) return rows.slice(0, 40);
    return rows
      .filter((x) => {
        const hay = [x.name, x.subtitle, x.keywords]
          .map((v) => String(v || "").toLowerCase())
          .join(" ");
        return hay.includes(needle);
      })
      .slice(0, 40);
  }, [items, q]);

  return (
    <div className="relative">
      <Label className="text-xs font-semibold mb-1 block">{label}</Label>
      <Input
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim() && value) {
            onChange("");
          }
        }}
        placeholder={placeholder}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">
              {noResultsText}
            </div>
          )}
          {filtered.map((x) => (
            <button
              type="button"
              key={x.id}
              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-b-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(String(x.id));
                setQ(x.name || "");
                setOpen(false);
              }}
            >
              <div className="text-sm font-medium text-slate-800">{x.name}</div>
              {x.subtitle ? (
                <div className="text-xs text-slate-500">{x.subtitle}</div>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SelectLookup({
  label,
  value,
  onChange,
  items,
  placeholder = "Select",
}) {
  return (
    <div>
      <Label className="text-xs font-semibold mb-1 block">{label}</Label>
      <select
        className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {(items || []).map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function LibraryBooksModule() {
  const { branchId } = useAuth();
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [racks, setRacks] = useState([]);
  const [form, setForm] = useState(emptyBook);
  const [openBookModal, setOpenBookModal] = useState(false);
  const [bookSearch, setBookSearch] = useState("");

  const load = async () => {
    const [b, a, p, c, r] = await Promise.all([
      api.get("/library/books", { params: { branch_id: branchId } }),
      api.get("/authors", { params: { branch_id: branchId, limit: 500 } }),
      api.get("/publishers", { params: { branch_id: branchId, limit: 500 } }),
      api.get("/book-categories", {
        params: { branch_id: branchId, limit: 500 },
      }),
      api.get("/racks", { params: { branch_id: branchId, limit: 500 } }),
    ]);
    setBooks(b.data.items || []);
    setAuthors(a.data.items || []);
    setPublishers(p.data.items || []);
    setCategories(c.data.items || []);
    setRacks(r.data.items || []);
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");

    const payload = {
      ...form,
      branch_id: branchId,
      total_qty: Number(form.total_qty || 0),
      available_qty:
        form.available_qty === "" || form.available_qty === null
          ? Number(form.total_qty || 0)
          : Number(form.available_qty),
      price: Number(form.price || 0),
    };

    if (form.id) {
      await api.put(`/library/books/${form.id}`, payload);
      toast.success("Book updated");
    } else {
      await api.post("/library/books", payload);
      toast.success("Book created");
    }

    setForm(emptyBook);
    load();
  };

  const edit = (row) => {
    setForm({
      id: row.id,
      title: row.title || "",
      author_id: row.author_id || "",
      publisher_id: row.publisher_id || "",
      category_id: row.category_id || "",
      rack_id: row.rack_id || "",
      code_isbn: row.code_isbn || "",
      total_qty: String(row.total_qty ?? 0),
      available_qty: String(row.available_qty ?? 0),
      price: String(row.price ?? 0),
    });
    setOpenBookModal(true);
  };

  const openCreateBook = () => {
    setForm(emptyBook);
    setOpenBookModal(true);
  };

  const filteredBooks = useMemo(() => {
    const q = bookSearch.trim().toLowerCase();
    if (!q) return books;
    return books.filter((b) => {
      const hay = [b.title, b.author_name, b.code_isbn]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [books, bookSearch]);

  const exportBooksCsv = () => {
    const headers = [
      "Title",
      "Author",
      "Publisher",
      "Category",
      "Rack",
      "Code/ISBN",
      "Total Qty",
      "Available Qty",
      "Price",
    ];
    const toCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredBooks.map((b) => [
      b.title,
      b.author_name,
      b.publisher_name,
      b.category_name,
      b.rack_name,
      b.code_isbn,
      b.total_qty,
      b.available_qty,
      b.price,
    ]);
    const csv = [
      headers.map(toCell).join(","),
      ...rows.map((r) => r.map(toCell).join(",")),
    ].join("\n");
    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `library-books-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3" data-testid="library-books-module">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Book Master</h3>
          <div className="flex gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={bookSearch}
                onChange={(e) => setBookSearch(e.target.value)}
                placeholder="Search title/code"
                className="pl-9"
              />
            </div>
            <Button className="erp-primary" onClick={openCreateBook}>
              <Plus className="w-4 h-4 mr-1" />
              Add New
            </Button>
            <Button variant="outline" onClick={exportBooksCsv}>
              Export Excel (CSV)
            </Button>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Author</th>
              <th className="text-left px-3 py-2">Code/ISBN</th>
              <th className="text-left px-3 py-2">Total Qty</th>
              <th className="text-left px-3 py-2">Available Qty</th>
              <th className="text-left px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredBooks.map((b) => (
              <tr key={b.id} className="border-t">
                <td className="px-3 py-2">{b.title}</td>
                <td className="px-3 py-2">{b.author_name || "-"}</td>
                <td className="px-3 py-2">{b.code_isbn || "-"}</td>
                <td className="px-3 py-2">{b.total_qty}</td>
                <td className="px-3 py-2 font-semibold">{b.available_qty}</td>
                <td className="px-3 py-2">
                  <Button variant="outline" size="sm" onClick={() => edit(b)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
            {filteredBooks.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No books found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={openBookModal}
        onOpenChange={(next) => setOpenBookModal(next)}
      >
        <DialogContent
          className="max-w-4xl"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Book" : "Add New Book"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Title</Label>
              <Input
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
              />
            </div>
            <SearchableSelect
              label="Author"
              value={form.author_id}
              onChange={(v) => setForm((f) => ({ ...f, author_id: v }))}
              items={(authors || []).map((x) => ({
                id: x.id,
                name: x.name,
                subtitle: `Author ID: ${x.id}`,
                keywords: `${x.name || ""} ${x.id || ""}`,
              }))}
              placeholder="Search author"
            />
            <SearchableSelect
              label="Publisher"
              value={form.publisher_id}
              onChange={(v) => setForm((f) => ({ ...f, publisher_id: v }))}
              items={(publishers || []).map((x) => ({
                id: x.id,
                name: x.name,
                subtitle: `Publisher ID: ${x.id}`,
                keywords: `${x.name || ""} ${x.id || ""}`,
              }))}
              placeholder="Search publisher"
            />
            <SearchableSelect
              label="Category"
              value={form.category_id}
              onChange={(v) => setForm((f) => ({ ...f, category_id: v }))}
              items={(categories || []).map((x) => ({
                id: x.id,
                name: x.name,
                subtitle: `Category ID: ${x.id}`,
                keywords: `${x.name || ""} ${x.id || ""}`,
              }))}
              placeholder="Search category"
            />
            <SearchableSelect
              label="Rack"
              value={form.rack_id}
              onChange={(v) => setForm((f) => ({ ...f, rack_id: v }))}
              items={(racks || []).map((x) => ({
                id: x.id,
                name: x.name,
                subtitle: x.location || `Rack ID: ${x.id}`,
                keywords: `${x.name || ""} ${x.location || ""} ${x.id || ""}`,
              }))}
              placeholder="Search rack"
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Code/ISBN
              </Label>
              <Input
                value={form.code_isbn}
                onChange={(e) =>
                  setForm((f) => ({ ...f, code_isbn: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Total Qty
              </Label>
              <Input
                type="number"
                min="0"
                value={form.total_qty}
                onChange={(e) =>
                  setForm((f) => ({ ...f, total_qty: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Available Qty
              </Label>
              <Input
                type="number"
                min="0"
                value={form.available_qty}
                onChange={(e) =>
                  setForm((f) => ({ ...f, available_qty: e.target.value }))
                }
                placeholder="Auto = Total Qty"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Price</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) =>
                  setForm((f) => ({ ...f, price: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpenBookModal(false)}>
              Close
            </Button>
            <Button
              className="erp-primary"
              onClick={async () => {
                await save();
                setOpenBookModal(false);
              }}
            >
              {form.id ? "Update Book" : "Create Book"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LibraryMembersModule() {
  const { branchId, sessionId } = useAuth();
  const [members, setMembers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(emptyMember);
  const [filters, setFilters] = useState({
    role: "all",
    classId: "all",
    section: "all",
    q: "",
  });
  const [openMemberModal, setOpenMemberModal] = useState(false);

  const loadMembers = async () => {
    try {
      const { data } = await api.get("/library/members", {
        params: { branch_id: branchId },
      });
      setMembers(data.items || []);
    } catch {
      toast.error("Failed to load library members");
      setMembers([]);
    }
  };

  const loadMasters = async () => {
    try {
      const [c, s, st] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId, limit: 1000 } }),
        api.get("/staff", { params: { branch_id: branchId, limit: 5000 } }),
        api.get("/students", {
          params: {
            branch_id: branchId,
            session_id: sessionId || undefined,
            limit: 5000,
          },
        }),
      ]);
      setClasses(c.data.items || []);
      setStaff(s.data.items || []);
      setAllStudents(st.data.items || []);
    } catch {
      toast.error("Failed to load members master data");
      setClasses([]);
      setStaff([]);
      setAllStudents([]);
    }
  };

  const loadSections = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }
    try {
      const { data } = await api.get("/sections", {
        params: { class_id: classId },
      });
      setSections(data.items || []);
    } catch {
      toast.error("Failed to load sections");
      setSections([]);
    }
  };

  const loadStudents = async (classId, sectionId) => {
    if (!classId || !sectionId) {
      setStudents([]);
      return;
    }
    try {
      const sectionObj = (sections || []).find(
        (s) => String(s.id) === String(sectionId),
      );
      const sectionName =
        sectionObj?.name || sectionObj?.section_name || String(sectionId);

      const baseParams = {
        branch_id: branchId,
        class_id: classId,
        section: sectionName,
        section_id: sectionId,
        limit: 2000,
      };

      let { data } = await api.get("/students", {
        params: {
          ...baseParams,
          session_id: sessionId || undefined,
        },
      });

      // Some databases don't map session rows strictly; fallback avoids empty dropdown.
      if ((data?.items || []).length === 0 && sessionId) {
        const retry = await api.get("/students", { params: baseParams });
        data = retry.data;
      }

      setStudents(data.items || []);
    } catch {
      toast.error("Failed to load students");
      setStudents([]);
    }
  };

  useEffect(() => {
    loadMembers();
    loadMasters();
  }, [branchId]);

  useEffect(() => {
    if (form.member_role !== "student") {
      setSections([]);
      setStudents([]);
      return;
    }
    loadSections(form.class_id);
  }, [form.member_role, form.class_id]);

  useEffect(() => {
    if (form.member_role !== "student") {
      setStudents([]);
      return;
    }
    loadStudents(form.class_id, form.section_id);
  }, [form.member_role, form.class_id, form.section_id, branchId, sessionId]);

  const selectedStudent = useMemo(
    () =>
      students.find((x) => String(x.id) === String(form.student_id)) || null,
    [students, form.student_id],
  );

  const selectedStaff = useMemo(
    () => staff.find((x) => String(x.id) === String(form.staff_id)) || null,
    [staff, form.staff_id],
  );

  const resolvedName = useMemo(() => {
    if (form.member_role === "student" && selectedStudent) {
      const first = selectedStudent.first_name || "";
      const last = selectedStudent.last_name || "";
      return `${first} ${last}`.trim() || selectedStudent.name || "";
    }
    if (form.member_role === "staff" && selectedStaff) {
      const first = selectedStaff.first_name || "";
      const last = selectedStaff.last_name || "";
      return `${first} ${last}`.trim() || selectedStaff.name || "";
    }
    return "";
  }, [form.member_role, selectedStudent, selectedStaff]);

  const save = async () => {
    if (form.member_role === "student" && !form.student_id) {
      return toast.error("Student ID is required for student member");
    }
    if (form.member_role === "staff" && !form.staff_id) {
      return toast.error("Staff ID is required for staff member");
    }

    const selectedPerson =
      form.member_role === "student" ? selectedStudent : selectedStaff;
    const autoCode = makeMemberCode(form.member_role, selectedPerson || {});
    const autoPhoto = normalizeImage(
      selectedPerson?.photo_url ||
        selectedPerson?.photo ||
        selectedPerson?.image ||
        selectedPerson?.avatar ||
        "",
    );

    const payload = {
      member_role: form.member_role,
      student_id: form.member_role === "student" ? form.student_id : undefined,
      staff_id: form.member_role === "staff" ? form.staff_id : undefined,
      name: resolvedName || undefined,
      member_code: form.member_code || autoCode || undefined,
      photo_url: autoPhoto || undefined,
      branch_id: branchId,
      max_active_issues:
        form.max_active_issues === ""
          ? undefined
          : Number(form.max_active_issues),
    };

    try {
      await api.post("/library/members", payload);
      toast.success("Member created");
      setForm(emptyMember);
      setOpenMemberModal(false);
      loadMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create member");
    }
  };

  const classMap = useMemo(() => {
    const map = new Map();
    (classes || []).forEach((c) => {
      map.set(String(c.id), c.name || c.class_name || `Class ${c.id}`);
    });
    return map;
  }, [classes]);

  const studentMap = useMemo(() => {
    const map = new Map();
    (allStudents || []).forEach((s) => {
      map.set(String(s.id), s);
    });
    return map;
  }, [allStudents]);

  const staffMap = useMemo(() => {
    const map = new Map();
    (staff || []).forEach((s) => {
      map.set(String(s.id), s);
    });
    return map;
  }, [staff]);

  const enrichedMembers = useMemo(() => {
    return (members || []).map((m) => {
      const role = String(
        m.member_role || m.member_type || "student",
      ).toLowerCase();
      const s =
        role === "student" && m.student_id
          ? studentMap.get(String(m.student_id))
          : null;
      const st =
        role === "staff" && m.staff_id
          ? staffMap.get(String(m.staff_id))
          : null;

      const studentName = s
        ? `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.name || ""
        : "";
      const staffName = st
        ? `${st.first_name || ""} ${st.last_name || ""}`.trim() || st.name || ""
        : "";

      const name =
        m.name || (role === "student" ? studentName : staffName) || "-";
      const classId = String(s?.class_id || "");
      const className = classMap.get(classId) || "-";
      const section = String(s?.section || s?.section_id || "") || "-";
      const mobile =
        role === "student"
          ? s?.mobile || s?.father_mobile || s?.guardian_mobile || "-"
          : st?.mobile || "-";
      const email =
        role === "student"
          ? s?.email || s?.father_email || s?.mother_email || "-"
          : st?.email || "-";
      const detail =
        role === "student"
          ? s?.admission_no || "-"
          : st?.designation || st?.designation_id || st?.role || "-";

      return {
        ...m,
        role,
        display_name: name,
        class_id: classId,
        class_name: className,
        section_name: section,
        mobile,
        email,
        detail,
      };
    });
  }, [members, studentMap, staffMap, classMap]);

  const sectionFilterOptions = useMemo(() => {
    const rows =
      filters.classId === "all"
        ? enrichedMembers
        : enrichedMembers.filter(
            (m) => String(m.class_id) === String(filters.classId),
          );
    return Array.from(
      new Set(
        rows
          .map((m) => String(m.section_name || "").trim())
          .filter((x) => x && x !== "-"),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [enrichedMembers, filters.classId]);

  const filteredMembers = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return enrichedMembers.filter((m) => {
      if (filters.role !== "all" && m.role !== filters.role) return false;
      if (
        filters.classId !== "all" &&
        String(m.class_id) !== String(filters.classId)
      )
        return false;
      if (
        filters.section !== "all" &&
        String(m.section_name) !== String(filters.section)
      )
        return false;
      if (!q) return true;

      const hay = [
        m.display_name,
        m.mobile,
        m.email,
        m.member_code,
        m.card_no,
        m.class_name,
        m.section_name,
        m.detail,
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [enrichedMembers, filters]);

  const exportMembersCsv = () => {
    const headers = [
      "Name",
      "Role",
      "Class",
      "Section",
      "Mobile",
      "Email",
      "Member Code",
      "Policy Limit",
      "Detail",
    ];

    const toCell = (v) => {
      const t = String(v ?? "").replace(/"/g, '""');
      return `"${t}"`;
    };

    const rows = filteredMembers.map((m) => [
      m.display_name,
      m.role,
      m.class_name,
      m.section_name,
      m.mobile,
      m.email,
      m.member_code || m.card_no || "",
      m.max_active_issues,
      m.detail,
    ]);

    const csv = [
      headers.map(toCell).join(","),
      ...rows.map((r) => r.map(toCell).join(",")),
    ].join("\n");
    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `library-members-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3" data-testid="library-members-module">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">
            Member Master
          </h3>
          <Button
            className="erp-primary"
            onClick={() => {
              setForm(emptyMember);
              setOpenMemberModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Member
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <div className="p-3 border-b bg-slate-50/70 grid grid-cols-1 md:grid-cols-5 gap-2">
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Filter Role
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={filters.role}
              onChange={(e) =>
                setFilters((f) => ({ ...f, role: e.target.value }))
              }
            >
              <option value="all">All</option>
              <option value="student">Student</option>
              <option value="staff">Staff</option>
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Filter Class
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={filters.classId}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  classId: e.target.value,
                  section: "all",
                }))
              }
            >
              <option value="all">All</option>
              {(classes || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.class_name || `Class ${c.id}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Filter Section
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={filters.section}
              onChange={(e) =>
                setFilters((f) => ({ ...f, section: e.target.value }))
              }
            >
              <option value="all">All</option>
              {sectionFilterOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Search</Label>
            <Input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Name / mobile / email / code"
            />
          </div>
          <div className="flex items-end">
            <Button className="erp-primary w-full" onClick={exportMembersCsv}>
              Export Excel (CSV)
            </Button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Class</th>
              <th className="text-left px-3 py-2">Section</th>
              <th className="text-left px-3 py-2">Mobile</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Member Code</th>
              <th className="text-left px-3 py-2">Policy Limit</th>
              <th className="text-left px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="px-3 py-2">{m.display_name || "-"}</td>
                <td className="px-3 py-2 capitalize">{m.role}</td>
                <td className="px-3 py-2">{m.class_name || "-"}</td>
                <td className="px-3 py-2">{m.section_name || "-"}</td>
                <td className="px-3 py-2">{m.mobile || "-"}</td>
                <td className="px-3 py-2">{m.email || "-"}</td>
                <td className="px-3 py-2">
                  {m.member_code || m.card_no || "-"}
                </td>
                <td className="px-3 py-2">{m.max_active_issues}</td>
                <td className="px-3 py-2">{m.detail || "-"}</td>
              </tr>
            ))}
            {filteredMembers.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={openMemberModal}
        onOpenChange={(next) => setOpenMemberModal(next)}
      >
        <DialogContent
          className="max-w-4xl"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Add Library Member</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Role</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.member_role}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    member_role: e.target.value,
                    student_id: "",
                    staff_id: "",
                    class_id: "",
                    section_id: "",
                  }))
                }
              >
                <option value="student">Student</option>
                <option value="staff">Teacher/Staff</option>
              </select>
            </div>
            {form.member_role === "student" ? (
              <>
                <SelectLookup
                  label="Class"
                  value={form.class_id}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      class_id: v,
                      section_id: "",
                      student_id: "",
                    }))
                  }
                  items={(classes || []).map((c) => ({
                    id: c.id,
                    name: c.name || c.class_name || `Class ${c.id}`,
                  }))}
                  placeholder="Select class"
                />
                <SelectLookup
                  label="Section"
                  value={form.section_id}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, section_id: v, student_id: "" }))
                  }
                  items={(sections || []).map((s) => ({
                    id: s.id,
                    name: s.name || s.section_name || `Section ${s.id}`,
                  }))}
                  placeholder="Select section"
                />
                <SearchableSelect
                  label="Search Student"
                  value={form.student_id}
                  onChange={(v) => setForm((f) => ({ ...f, student_id: v }))}
                  items={(students || []).map((s) => ({
                    id: s.id,
                    name:
                      `${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                      s.name ||
                      `Student ${s.id}`,
                    subtitle: `${s.admission_no || "-"} · ${s.mobile || s.father_mobile || s.guardian_mobile || "No mobile"}`,
                    keywords: `${s.admission_no || ""} ${s.mobile || ""} ${s.father_mobile || ""}`,
                  }))}
                  placeholder={
                    form.class_id && form.section_id
                      ? "Search student by name or admission"
                      : "Select class and section first"
                  }
                />
              </>
            ) : (
              <SearchableSelect
                label="Search Staff"
                value={form.staff_id}
                onChange={(v) => setForm((f) => ({ ...f, staff_id: v }))}
                items={(staff || []).map((s) => ({
                  id: s.id,
                  name:
                    `${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                    s.name ||
                    `Staff ${s.id}`,
                  subtitle: `${s.mobile || "No mobile"} · ${s.staff_id || s.staff_no || "No code"}`,
                  keywords: `${s.mobile || ""} ${s.staff_id || ""} ${s.staff_no || ""}`,
                }))}
                placeholder="Search staff by name or mobile"
              />
            )}
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Member Name
              </Label>
              <Input
                value={resolvedName}
                readOnly
                placeholder="Auto from selected member"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Library Card / Member Code
              </Label>
              <Input
                value={form.member_code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, member_code: e.target.value }))
                }
                placeholder="Auto generated if left blank"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Max Active Issues
              </Label>
              <Input
                type="number"
                min="1"
                value={form.max_active_issues}
                placeholder={
                  form.member_role === "student" ? "Default 2" : "Default 5"
                }
                onChange={(e) =>
                  setForm((f) => ({ ...f, max_active_issues: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpenMemberModal(false)}>
              Close
            </Button>
            <Button className="erp-primary" onClick={save}>
              Create Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LibraryIssueReturnModule() {
  const { branchId, sessionId } = useAuth();
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [issues, setIssues] = useState([]);
  const [issueFilter, setIssueFilter] = useState("active");
  const [issueSearch, setIssueSearch] = useState("");
  const [openIssueModal, setOpenIssueModal] = useState(false);
  const [pendingReturnIssue, setPendingReturnIssue] = useState(null);
  const [form, setForm] = useState({
    book_id: "",
    member_id: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    fine_per_day: "5",
  });

  const load = async () => {
    try {
      const [b, m, s, st, i] = await Promise.all([
        api.get("/library/books", { params: { branch_id: branchId } }),
        api.get("/library/members", { params: { branch_id: branchId } }),
        api.get("/students", {
          params: {
            branch_id: branchId,
            session_id: sessionId || undefined,
            limit: 5000,
          },
        }),
        api.get("/staff", { params: { branch_id: branchId, limit: 5000 } }),
        api.get("/library/issues", { params: { status: "all" } }),
      ]);
      setBooks(b.data.items || []);
      setMembers(m.data.items || []);
      setStudents(s.data.items || []);
      setStaff(st.data.items || []);
      setIssues(i.data.items || []);
    } catch {
      toast.error("Failed to load issue/return data");
      setIssues([]);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, sessionId]);

  const issueBook = async () => {
    if (!form.book_id || !form.member_id) {
      return toast.error("Select both Book and Member");
    }
    try {
      await api.post("/library/issues/issue", {
        ...form,
        branch_id: branchId,
        fine_per_day: Number(form.fine_per_day || 5),
      });
      toast.success("Book issued");
      setOpenIssueModal(false);
      setForm((f) => ({
        ...f,
        book_id: "",
        member_id: "",
      }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed");
    }
  };

  const returnBook = async (id) => {
    try {
      await api.post(`/library/issues/${id}/return`, {
        return_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Book returned");
      setPendingReturnIssue(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Return failed");
    }
  };

  const availableBooks = useMemo(
    () => books.filter((b) => Number(b.available_qty || 0) > 0),
    [books],
  );

  const studentMap = useMemo(() => {
    const map = new Map();
    (students || []).forEach((s) => map.set(String(s.id), s));
    return map;
  }, [students]);

  const staffMap = useMemo(() => {
    const map = new Map();
    (staff || []).forEach((s) => map.set(String(s.id), s));
    return map;
  }, [staff]);

  const memberOptions = useMemo(
    () =>
      members.map((m) => {
        const role = String(
          m.member_role || m.member_type || "member",
        ).toLowerCase();
        const s = m.student_id ? studentMap.get(String(m.student_id)) : null;
        const st = m.staff_id ? staffMap.get(String(m.staff_id)) : null;

        const resolvedName =
          m.name ||
          (role === "student"
            ? `${s?.first_name || ""} ${s?.last_name || ""}`.trim() ||
              s?.name ||
              ""
            : `${st?.first_name || ""} ${st?.last_name || ""}`.trim() ||
              st?.name ||
              "") ||
          `Member ${m.id}`;

        const code =
          m.member_code ||
          m.card_no ||
          s?.admission_no ||
          st?.staff_id ||
          st?.staff_no ||
          "-";

        return {
          id: m.id,
          name: resolvedName,
          subtitle: `${code} · ${role}`,
          keywords: `${resolvedName} ${code} ${role}`,
        };
      }),
    [members, studentMap, staffMap],
  );

  const filteredIssues = useMemo(() => {
    const q = issueSearch.trim().toLowerCase();
    return (issues || []).filter((it) => {
      const isReturned = !!it.return_date && it.return_date !== "0000-00-00";

      if (issueFilter === "active" && isReturned) return false;
      if (issueFilter === "returned" && !isReturned) return false;
      if (issueFilter === "overdue") {
        const due = String(it.due_date || "");
        const today = new Date().toISOString().slice(0, 10);
        if (isReturned || !due || due >= today) return false;
      }

      if (!q) return true;
      const hay = [
        it.book_title,
        it.book_code,
        it.member_name,
        it.member_code,
        it.issue_date,
        it.due_date,
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [issues, issueFilter, issueSearch]);

  return (
    <div className="space-y-3" data-testid="library-issue-return-module">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">
            Issue / Return
          </h3>
          <Button
            className="erp-primary"
            onClick={() => setOpenIssueModal(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            New Issue
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <div className="p-3 border-b bg-slate-50/70 grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Issue Filter
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={issueFilter}
              onChange={(e) => setIssueFilter(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="returned">Returned</option>
              <option value="overdue">Overdue</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs font-semibold mb-1 block">
              Search Issues
            </Label>
            <Input
              value={issueSearch}
              onChange={(e) => setIssueSearch(e.target.value)}
              placeholder="Book / member / dates"
            />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Book</th>
              <th className="text-left px-3 py-2">Member</th>
              <th className="text-left px-3 py-2">Issue Date</th>
              <th className="text-left px-3 py-2">Due Date</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredIssues.map((it) => (
              <tr key={it.id} className="border-t">
                <td className="px-3 py-2">
                  {it.book_title || `#${it.book_id}`}
                </td>
                <td className="px-3 py-2">
                  {it.member_name || it.member_code || `#${it.member_id}`}
                </td>
                <td className="px-3 py-2">{it.issue_date}</td>
                <td className="px-3 py-2">{it.due_date}</td>
                <td className="px-3 py-2">
                  {it.return_date && it.return_date !== "0000-00-00"
                    ? "Returned"
                    : "Issued"}
                </td>
                <td className="px-3 py-2">
                  {it.return_date && it.return_date !== "0000-00-00" ? (
                    <span className="text-slate-400">Done</span>
                  ) : (
                    <Button
                      size="sm"
                      className="erp-primary"
                      onClick={() => setPendingReturnIssue(it)}
                    >
                      Return
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filteredIssues.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No issues found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={openIssueModal}
        onOpenChange={(next) => setOpenIssueModal(next)}
      >
        <DialogContent
          className="max-w-4xl"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Issue Book</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SearchableSelect
              label="Book"
              value={form.book_id}
              onChange={(v) => setForm((f) => ({ ...f, book_id: v }))}
              items={availableBooks.map((b) => ({
                id: b.id,
                name: b.title,
                subtitle: `Available: ${b.available_qty} · Code: ${b.code_isbn || "-"}`,
                keywords: `${b.title} ${b.code_isbn || ""}`,
              }))}
              placeholder="Search book by title or code"
            />
            <SearchableSelect
              label="Member"
              value={form.member_id}
              onChange={(v) => setForm((f) => ({ ...f, member_id: v }))}
              items={memberOptions}
              placeholder="Search member by name or card code"
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Fine / day
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.fine_per_day}
                onChange={(e) =>
                  setForm((f) => ({ ...f, fine_per_day: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Issue Date
              </Label>
              <Input
                type="date"
                value={form.issue_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, issue_date: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Due Date
              </Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, due_date: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpenIssueModal(false)}>
              Close
            </Button>
            <Button className="erp-primary" onClick={issueBook}>
              Issue Book
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingReturnIssue}
        onOpenChange={(next) => {
          if (!next) setPendingReturnIssue(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm return?</AlertDialogTitle>
            <AlertDialogDescription>
              Return {pendingReturnIssue?.book_title || "this book"} for{" "}
              {pendingReturnIssue?.member_name ||
                pendingReturnIssue?.member_code ||
                "this member"}
              .
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingReturnIssue(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="erp-primary"
              onClick={() =>
                pendingReturnIssue && returnBook(pendingReturnIssue.id)
              }
            >
              Confirm Return
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function LibraryCardBlueprint() {
  const { branchId, sessionId } = useAuth();
  const [members, setMembers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [design, setDesign] = useState("design1");
  const [schoolLogoUrl, setSchoolLogoUrl] = useState("");
  const [schoolName, setSchoolName] = useState("School ERP");
  const [cardTerms, setCardTerms] = useState([
    "This card is non-transferable and must be presented during checkouts.",
    "A maximum of 2 books can be issued at a time for 14 days.",
    "An overdue fine of Rs.5 per day will be applicable on late returns.",
    "Loss of card must be reported immediately. Replacement fee is Rs.100.",
  ]);

  const loadData = async () => {
    try {
      const [m, c, s, schoolRes, cardCfgRes] = await Promise.all([
        api.get("/library/members", {
          params: { branch_id: branchId, limit: 5000 },
        }),
        api.get("/classes", { params: { branch_id: branchId, limit: 1000 } }),
        api.get("/students", {
          params: {
            branch_id: branchId,
            session_id: sessionId || undefined,
            limit: 5000,
          },
        }),
        api.get("/settings/school_info").catch(() => ({ data: { value: {} } })),
        api
          .get("/settings/library_card_config")
          .catch(() => ({ data: { value: {} } })),
      ]);
      setMembers(m.data.items || []);
      setClasses(c.data.items || []);
      setStudents(s.data.items || []);

      const schoolValue = schoolRes?.data?.value || {};
      const configValue = cardCfgRes?.data?.value || {};

      const resolvedSchoolName = String(
        configValue.school_name || schoolValue.school_name || "School ERP",
      ).trim();
      setSchoolName(resolvedSchoolName || "School ERP");

      const logoFromSettings =
        configValue.logo_url || schoolValue.logo_url || schoolValue.logo || "";
      const resolvedLogo =
        String(logoFromSettings || "").trim() === ""
          ? ""
          : /^https?:\/\//i.test(String(logoFromSettings)) ||
              String(logoFromSettings).startsWith("/")
            ? String(logoFromSettings)
            : buildFileUrl(String(logoFromSettings));
      if (resolvedLogo) {
        setSchoolLogoUrl(resolvedLogo);
      }

      const cfgTerms = Array.isArray(configValue.terms)
        ? configValue.terms
        : [
            configValue.term_1,
            configValue.term_2,
            configValue.term_3,
            configValue.term_4,
          ];

      const normalizedTerms = (cfgTerms || [])
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .slice(0, 4);

      if (normalizedTerms.length > 0) {
        setCardTerms(normalizedTerms);
      }
    } catch {
      toast.error("Failed to load card data");
    }
  };

  const saveCardTerms = async () => {
    const terms = (cardTerms || [])
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    if (terms.length === 0) {
      toast.error("Please enter at least one term");
      return;
    }
    try {
      await api.put("/settings/library_card_config", {
        value: { terms },
      });
      toast.success("Card terms saved");
    } catch {
      toast.error("Failed to save card terms");
    }
  };

  useEffect(() => {
    loadData();
  }, [branchId, sessionId]);

  const enriched = useMemo(() => {
    const studentMap = new Map((students || []).map((s) => [String(s.id), s]));
    return (members || []).map((m) => {
      const s = m.student_id ? studentMap.get(String(m.student_id)) : null;
      const classId = String(s?.class_id || "");
      const photoSource =
        m.photo_url ||
        s?.photo_url ||
        extractImageValue(s?.documents) ||
        extractImageValue(s?.payload_json) ||
        extractImageValue(s?.doc_student_photo) ||
        extractImageValue(s?.student_photo) ||
        s?.photo ||
        s?.image ||
        "";
      const photo = normalizeImage(photoSource);
      const cardCode =
        m.member_code ||
        m.card_no ||
        makeMemberCode(m.member_role || "student", s || m);
      const displayName =
        m.name || `${s?.first_name || ""} ${s?.last_name || ""}`.trim() || "-";
      return {
        ...m,
        name: displayName,
        class_id: classId,
        roll_no: s?.roll_no || s?.roll_number || "-",
        admission_no: s?.admission_no || cardCode || "-",
        section: s?.section || s?.section_id || "",
        photo_url: photo,
        card_code: cardCode,
      };
    });
  }, [members, students]);

  const classMembers = useMemo(() => {
    if (!selectedClass) return [];
    return enriched.filter((m) => String(m.class_id) === String(selectedClass));
  }, [enriched, selectedClass]);

  const classLabel = useMemo(() => {
    const c = (classes || []).find(
      (x) => String(x.id) === String(selectedClass),
    );
    return c?.name || c?.class_name || "";
  }, [classes, selectedClass]);

  const qrDataUrl = (member) => {
    const payload = encodeURIComponent(
      JSON.stringify({
        id: member.id,
        code: member.card_code,
        name: member.name,
        class: classLabel,
        section: member.section,
      }),
    );
    return `https://api.qrserver.com/v1/create-qr-code/?size=72x72&data=${payload}`;
  };

  return (
    <div data-testid="library-card-blueprint" className="space-y-3">
      <style>{`
        .lib-print-sheet {
          width: 100%;
        }
        .lib-print-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 10px;
        }
        .lib-pair {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          break-inside: avoid;
        }
        .lib-card {
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #cbd5e1;
          overflow: hidden;
          min-height: 190px;
          box-shadow: 0 6px 20px rgba(15, 23, 42, 0.1);
        }
        .lib-front-head {
          background: linear-gradient(90deg, #1947b6, #3b82f6);
          color: #fff;
          padding: 8px 12px;
          display: grid;
          grid-template-columns: 28px 1fr;
          gap: 8px;
          align-items: center;
        }
        .lib-front.design2 .lib-front-head {
          background: linear-gradient(90deg, #7c2d12, #ea580c);
        }
        .lib-front.design3 .lib-front-head {
          background: linear-gradient(90deg, #0f766e, #06b6d4);
        }
        .lib-logo-dot {
          width: 28px;
          height: 28px;
          border-radius: 999px;
          overflow: hidden;
          background: rgba(255,255,255,0.95);
          color: #1e3a8a;
          display: grid;
          place-items: center;
          font-size: 12px;
          font-weight: 700;
        }
        .lib-logo-dot img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lib-front-school {
          font-size: 14px;
          font-weight: 800;
        }
        .lib-front-sub {
          font-size: 10px;
          opacity: 0.95;
        }
        .lib-front-body {
          display: grid;
          grid-template-columns: 74px 1fr;
          gap: 12px;
          padding: 10px 12px;
        }
        .lib-photo {
          width: 74px;
          height: 92px;
          border-radius: 6px;
          border: 1px dashed #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          text-align: center;
          background: #ffffff;
          color: #64748b;
        }
        .lib-front-meta {
          display: grid;
          gap: 3px;
          font-size: 11px;
        }
        .lib-front-row {
          display: grid;
          grid-template-columns: 66px 1fr;
          gap: 6px;
          align-items: baseline;
        }
        .lib-front-label {
          color: #334155;
          font-weight: 600;
        }
        .lib-front-value {
          font-weight: 700;
          color: #0f172a;
        }
        .lib-front-foot {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 12px 8px;
          border-top: 1px dashed #cbd5e1;
        }
        .lib-qr-wrap {
          width: 54px;
          height: 54px;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          background: #fff;
          padding: 2px;
          display: grid;
          place-items: center;
        }
        .lib-front-code-space {
          flex: 1;
        }
        .lib-back {
          display: flex;
          flex-direction: column;
        }
        .lib-back-head {
          font-size: 13px;
          font-weight: 800;
          color: #1e40af;
          border-bottom: 1px solid #cbd5e1;
          padding: 10px 12px 8px;
        }
        .lib-terms {
          padding: 8px 14px;
          margin: 0;
          color: #334155;
          font-size: 10px;
          line-height: 1.5;
        }
        .lib-terms li {
          margin-bottom: 3px;
        }
        .lib-back-foot {
          margin-top: auto;
          padding: 8px 12px 10px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          color: #64748b;
          font-size: 10px;
        }
        .lib-sign-line {
          width: 90px;
          border-top: 1px solid #64748b;
          text-align: center;
          padding-top: 2px;
          color: #0f172a;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          .print-library-card,
          .print-library-card * {
            visibility: visible;
          }
          .print-library-card {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: #fff !important;
            padding: 6mm 5mm;
          }
          .lib-print-sheet {
            width: 210mm;
            min-height: 297mm;
          }
          .lib-print-grid {
            gap: 4mm;
          }
          .lib-pair {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            min-height: 67mm;
            gap: 4mm;
            break-inside: avoid;
          }
          .lib-card {
            box-shadow: none;
            break-inside: avoid;
          }
          .lib-page-break {
            break-before: page;
          }
        }
      `}</style>

      <div className="border border-slate-200 bg-white rounded-md p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">
          Library Card Generator (Class-wise)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <SelectLookup
              label="Class"
              value={selectedClass}
              onChange={setSelectedClass}
              items={(classes || []).map((c) => ({
                id: c.id,
                name: c.name || c.class_name || `Class ${c.id}`,
              }))}
              placeholder="Select class"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Card Design
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={design}
              onChange={(e) => setDesign(e.target.value)}
            >
              <option value="design1">Design 1 - Emerald</option>
              <option value="design2">Design 2 - Amber</option>
              <option value="design3">Design 3 - Ocean</option>
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              Selected Class
            </Label>
            <Input value={classLabel || "-"} readOnly />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              School Logo URL (optional override)
            </Label>
            <Input
              value={schoolLogoUrl}
              onChange={(e) => setSchoolLogoUrl(e.target.value)}
              placeholder="Optional logo URL"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">
              School Name (From Settings)
            </Label>
            <Input value={schoolName} readOnly />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs font-semibold mb-1 block">
              Terms & Conditions (Printable)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Input
                  key={i}
                  value={cardTerms[i] || ""}
                  onChange={(e) =>
                    setCardTerms((prev) => {
                      const next = [...prev];
                      next[i] = e.target.value;
                      return next;
                    })
                  }
                  placeholder={`Term ${i + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full"
              onClick={saveCardTerms}
            >
              Save Terms
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              className="erp-primary w-full"
              disabled={!selectedClass || classMembers.length === 0}
              onClick={() => window.print()}
            >
              Print Class Cards ({classMembers.length})
            </Button>
          </div>
        </div>
      </div>

      <div className="print-library-card lib-print-sheet">
        <div className="lib-print-grid">
          {classMembers.map((m, idx) => (
            <div
              key={m.id}
              className={`lib-pair ${idx > 0 && idx % 4 === 0 ? "lib-page-break" : ""}`}
            >
              <div className={`lib-card lib-front ${design}`}>
                <div className="lib-front-head">
                  <div className="lib-logo-dot">
                    {schoolLogoUrl.trim() ? (
                      <img src={schoolLogoUrl} alt="School Logo" />
                    ) : (
                      <span>S</span>
                    )}
                  </div>
                  <div>
                    <div className="lib-front-school">
                      {schoolName || "School ERP"}
                    </div>
                    <div className="lib-front-sub">
                      Library Identification Card
                    </div>
                  </div>
                </div>
                <div className="lib-front-body">
                  <div className="lib-photo">
                    {m.photo_url ? (
                      <img
                        src={m.photo_url}
                        alt={m.name || "member"}
                        className="w-full h-full object-cover rounded-[5px]"
                      />
                    ) : (
                      "PASSPORT PHOTO"
                    )}
                  </div>
                  <div className="lib-front-meta">
                    <div className="lib-front-row">
                      <span className="lib-front-label">Name:</span>
                      <span className="lib-front-value">{m.name || "-"}</span>
                    </div>
                    <div className="lib-front-row">
                      <span className="lib-front-label">Roll No:</span>
                      <span className="lib-front-value">
                        {m.roll_no || "-"}
                      </span>
                    </div>
                    <div className="lib-front-row">
                      <span className="lib-front-label">Class/Sec:</span>
                      <span className="lib-front-value">
                        {classLabel || "-"}
                        {m.section ? ` - ${m.section}` : ""}
                      </span>
                    </div>
                    <div className="lib-front-row">
                      <span className="lib-front-label">Adm No:</span>
                      <span className="lib-front-value">
                        {m.admission_no || "-"}
                      </span>
                    </div>
                    <div className="lib-front-row">
                      <span className="lib-front-label">Validity:</span>
                      <span className="lib-front-value">2026 - 2027</span>
                    </div>
                  </div>
                </div>
                <div className="lib-front-foot">
                  <div className="lib-front-code-space" />
                  <div className="lib-qr-wrap">
                    <img
                      src={qrDataUrl(m)}
                      alt="QR"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>

              <div className="lib-card lib-back">
                <div className="lib-back-head">TERMS &amp; CONDITIONS</div>
                <ul className="lib-terms">
                  {(cardTerms || []).filter(Boolean).map((term, tIdx) => (
                    <li key={`${m.id}-term-${tIdx}`}>{term}</li>
                  ))}
                </ul>
                <div className="lib-back-foot">
                  <span />
                  <span className="lib-sign-line">Librarian Sign</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
